import React, { useState, useEffect, useRef } from 'react';
import './DatingChatBot.css';
import { generateConversationalResponse, generateVibeAnalysis } from '../services/firebaseApiService';

const DatingChatBot = ({ 
  isOpen, 
  onClose, 
  userDescription, 
  onSaveConversation,
  currentUser,
  onOpenSignUp
}) => {
  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationComplete, setConversationComplete] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [vibeAnalysis, setVibeAnalysis] = useState('');
  const [editedAnalysis, setEditedAnalysis] = useState('');
  const [editedBookTitle, setEditedBookTitle] = useState('');
  const [editedAuthor, setEditedAuthor] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasStartedConversation = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const focusInput = () => {
    if (inputRef.current && !conversationComplete) {
      // Focus the element
      inputRef.current.focus();
      
      // Ensure cursor is visible and blinking by setting selection range
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
      
      // Force a click to ensure the cursor is active and blinking
      inputRef.current.click();
      
      // Double-focus approach to ensure cursor is blinking
      setTimeout(() => {
        if (inputRef.current && !conversationComplete) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(length, length);
        }
      }, 10);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && userDescription && !hasStartedConversation.current) {
      startConversation();
    }
  }, [isOpen, userDescription]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus input when chatbot opens
  useEffect(() => {
    if (isOpen && !conversationComplete) {
      setTimeout(focusInput, 200);
      // Additional focus after a longer delay to ensure cursor is blinking
      setTimeout(focusInput, 500);
    }
  }, [isOpen, conversationComplete]);

  // Disable main page scroll and hide navbar when chatbot is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      
      // Disable scroll by setting body to fixed position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Hide navbar
      document.body.classList.add('chatbot-open');
      
      return () => {
        // Re-enable scroll when chatbot closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Show navbar
        document.body.classList.remove('chatbot-open');
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard detection for mobile
  useEffect(() => {
    if (!isOpen) return;

    let initialHeight = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
      // If height decreased significantly, keyboard is likely visible
      const heightDifference = initialHeight - currentHeight;
      const keyboardVisible = heightDifference > 150; // Threshold for keyboard detection
      
      setIsKeyboardVisible(keyboardVisible);
    };

    const handleFocus = () => {
      // When input is focused, assume keyboard will appear
      setTimeout(() => {
        const currentHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        const heightDifference = initialHeight - currentHeight;
        setIsKeyboardVisible(heightDifference > 100);
      }, 300);
    };

    const handleBlur = () => {
      // When input loses focus, keyboard might disappear
      setTimeout(() => {
        const currentHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        const heightDifference = initialHeight - currentHeight;
        setIsKeyboardVisible(heightDifference > 100);
      }, 300);
    };

    // Listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Listen for focus events on the input
    if (inputRef.current) {
      inputRef.current.addEventListener('focus', handleFocus);
      inputRef.current.addEventListener('blur', handleBlur);
    }

    // Initial check
    handleResize();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      if (inputRef.current) {
        inputRef.current.removeEventListener('focus', handleFocus);
        inputRef.current.removeEventListener('blur', handleBlur);
      }
    };
  }, [isOpen, inputRef.current]);

  const startConversation = async () => {
    if (hasStartedConversation.current) return; // Prevent multiple initial responses
    
    hasStartedConversation.current = true;
    
    const initialMessages = [
      {
        id: 1,
        type: 'user',
        content: userDescription,
        timestamp: new Date()
      }
    ];
    
    setMessages(initialMessages);
    setQuestionCount(0);
    
    // Generate first response to the vibe prompt
    await generateInitialResponse(initialMessages);
  };

  const generateInitialResponse = async (currentMessages) => {
    setIsLoading(true);
    
    try {
      const initialResponse = await generateConversationalResponse(
        currentMessages, 
        userDescription
      );
      
      if (initialResponse) {
        const botMessage = {
          id: Date.now(),
          type: 'bot',
          content: initialResponse,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, botMessage]);
        setQuestionCount(1); // Set to 1 since we just had our first exchange
        // Focus input after bot responds
        setTimeout(focusInput, 100);
      }
    } catch (error) {
      console.error('Error generating initial response:', error);
      // Fallback to generic response
      // Try to be specific to their description
      const userDesc = userDescription.toLowerCase();
      let fallbackContent;
      
      if (userDesc.includes('funny') || userDesc.includes('humor')) {
        fallbackContent = "Love that you value humor. What kind of humor hits different for you?";
      } else if (userDesc.includes('kind') || userDesc.includes('caring')) {
        fallbackContent = "Kindness is everything. How do you like to see that kindness in action?";
      } else if (userDesc.includes('adventure') || userDesc.includes('travel')) {
        fallbackContent = "Adventure sounds fire. What's your ideal adventure together?";
      } else {
        fallbackContent = "Thanks for sharing that. What draws you to that vibe?";
      }
      
      const fallbackMessage = {
        id: Date.now(),
        type: 'bot',
        content: fallbackContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, fallbackMessage]);
      setQuestionCount(1);
      // Focus input after fallback response
      setTimeout(focusInput, 100);
    } finally {
      setIsLoading(false);
      // Focus input after loading ends
      setTimeout(focusInput, 100);
    }
  };

  const generateNextResponse = async (currentMessages) => {
    setIsLoading(true);
    
    try {
      const nextResponse = await generateConversationalResponse(
        currentMessages, 
        userDescription
      );
      
      if (nextResponse) {
        const newMessage = {
          id: Date.now(),
          type: 'bot',
          content: nextResponse,
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, newMessage]);
        setQuestionCount(prev => prev + 1);
        // Focus input after bot responds
        setTimeout(focusInput, 100);
      } else {
        // End conversation if no more responses
        endConversation();
      }
    } catch (error) {
      console.error('Error generating response:', error);
      endConversation();
    } finally {
      setIsLoading(false);
      // Focus input after loading ends
      setTimeout(focusInput, 100);
    }
  };

  const handleSendMessage = async () => {
    if (!currentInput.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentInput.trim(),
      timestamp: new Date()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setCurrentInput('');
    
    // Focus input after sending message
    setTimeout(focusInput, 50);

    // Check if we should continue or end conversation
    if (questionCount >= 9) {
      endConversation();
    } else {
      // Generate next conversational response
      await generateNextResponse(updatedMessages);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const endConversation = async () => {
    // First, show the "Thanks for chatting" message
    const thankYouMessage = {
      id: Date.now(),
      type: 'bot',
      content: "Thanks for chatting. Analyzing your responses...",
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, thankYouMessage]);
    setConversationComplete(true);
    setIsLoading(true);
    
    try {
      // Generate personalized vibe analysis
      const analysisResult = await generateVibeAnalysis(messages, userDescription);
      setVibeAnalysis(analysisResult.response);
      setBookTitle(analysisResult.bookTitle);
      setAuthor(analysisResult.author);
      
      // Add the vibe analysis as a special message in the chat
      const vibeMessage = {
        id: Date.now() + 1,
        type: 'vibe',
        content: analysisResult.response,
        bookTitle: analysisResult.bookTitle,
        author: analysisResult.author,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, vibeMessage]);
      
      // Save conversation to Firebase
      if (currentUser && onSaveConversation) {
        const conversationData = {
          userDescription,
          messages: [...messages, thankYouMessage, vibeMessage],
          questionCount,
          timestamp: new Date(),
          userId: currentUser.uid,
          vibeAnalysis: analysisResult.response,
          bookTitle: analysisResult.bookTitle,
          author: analysisResult.author
        };
        onSaveConversation(conversationData);
      }
    } catch (error) {
      console.error('Error generating vibe analysis:', error);
      
      // Fallback analysis
      const fallbackAnalysis = 'BOOK_TITLE: The Seven Husbands of Evelyn Hugo\nAUTHOR: Taylor Jenkins Reid';
      const fallbackBookTitle = 'The Seven Husbands of Evelyn Hugo';
      const fallbackAuthor = 'Taylor Jenkins Reid';
      
      setVibeAnalysis(fallbackAnalysis);
      setBookTitle(fallbackBookTitle);
      setAuthor(fallbackAuthor);
      
      const vibeMessage = {
        id: Date.now() + 1,
        type: 'vibe',
        content: fallbackAnalysis,
        bookTitle: fallbackBookTitle,
        author: fallbackAuthor,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, vibeMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setMessages([]);
    setCurrentInput('');
    setConversationComplete(false);
    setQuestionCount(0);
    setVibeAnalysis('');
    setEditedAnalysis('');
    setEditedBookTitle('');
    setEditedAuthor('');
    setBookTitle('');
    setAuthor('');
    setIsConfirmed(false);
    setShowCompletionMessage(false);
    setEditingMessageId(null);
    hasStartedConversation.current = false;
  };

  const handleEditVibe = (messageId, currentContent, currentBookTitle, currentAuthor) => {
    setEditingMessageId(messageId);
    setEditedAnalysis(currentContent);
    setEditedBookTitle(currentBookTitle || '');
    setEditedAuthor(currentAuthor || '');
    // Reset confirm button and hide completion message when editing starts
    setIsConfirmed(false);
    setShowCompletionMessage(false);
  };

  const handleSaveEdit = () => {
    // Update the vibe message in the messages array
    setMessages(prev => prev.map(msg => 
      msg.id === editingMessageId 
        ? { 
            ...msg, 
            content: `BOOK_TITLE: ${editedBookTitle}\nAUTHOR: ${editedAuthor}`,
            bookTitle: editedBookTitle,
            author: editedAuthor
          }
        : msg
    ));
    
    // Update the state variables
    setVibeAnalysis(`BOOK_TITLE: ${editedBookTitle}\nAUTHOR: ${editedAuthor}`);
    setBookTitle(editedBookTitle);
    setAuthor(editedAuthor);
    
    // Reset editing state
    setEditingMessageId(null);
    setEditedAnalysis('');
    setEditedBookTitle('');
    setEditedAuthor('');
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedAnalysis('');
    setEditedBookTitle('');
    setEditedAuthor('');
    // Scroll to bottom after canceling edit
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  };

  const handleConfirm = () => {
    // Mark as confirmed first
    setIsConfirmed(true);
    
    // Show completion message
    setShowCompletionMessage(true);
    
    // Save the conversation and vibe analysis to user profile
    if (currentUser && onSaveConversation) {
      const conversationData = {
        userDescription,
        messages,
        questionCount,
        timestamp: new Date(),
        userId: currentUser.uid,
        vibeAnalysis: vibeAnalysis,
        bookTitle: bookTitle,
        author: author
      };
      onSaveConversation(conversationData);
    }

    // Scroll to bottom after showing completion message
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    // Button changes color but window stays open
  };

  const handleSignUp = () => {
    // Save the conversation and vibe analysis to user profile
    if (currentUser && onSaveConversation) {
      const conversationData = {
        userDescription,
        messages,
        questionCount,
        timestamp: new Date(),
        userId: currentUser.uid,
        vibeAnalysis: vibeAnalysis
      };
      onSaveConversation(conversationData);
    }

    // Don't close the chatbot, just open the sign up popup
    if (onOpenSignUp) {
      onOpenSignUp();
    }
  };

  const handleShare = () => {
    // Get the current vibe analysis from the messages if state is empty
    const vibeMessage = messages.find(msg => msg.type === 'vibe');
    const currentBookTitle = bookTitle || vibeMessage?.bookTitle || 'Unknown Book';
    const currentAuthor = author || vibeMessage?.author || 'Unknown Author';
    
    if (!currentBookTitle || !currentAuthor) {
      alert('No vibe analysis available to share. Please complete the conversation first.');
      return;
    }
    
    // Create the formatted share text
    const shareText = `My LUV vibe's giving "${currentBookTitle}" by ${currentAuthor}

www.luv.wtf`;
    
    // Check if Web Share API is supported
    if (navigator.share) {
      // Try sharing with just the text (no URL) to ensure text gets through
      navigator.share({
        title: 'My LUV Vibe',
        text: shareText
      }).catch((error) => {
        console.log('Error sharing with text only:', error);
        // Fallback to clipboard
        copyToClipboard(shareText);
      });
    } else {
      // Fallback to clipboard for browsers that don't support Web Share API
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Text copied silently
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      // Text copied silently
    });
  };

  return (
    <>
    {isOpen && (
    <div className="dating-chatbot-overlay" onClick={handleClose}>
      <div className={`dating-chatbot-container ${isKeyboardVisible ? 'keyboard-visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="chatbot-header">
          <div className="chatbot-title">
            <div className="chatbot-avatar">
              <img src="/heartintel.svg" alt="LUV" className="chatbot-logo" />
            </div>
            <div>
              <h3>Create Your LUV</h3>
              <p>Your responses train your LUV</p>
            </div>
          </div>
          <button className="chatbot-close" onClick={handleClose}>×</button>
        </div>

        <div className="chatbot-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.type}`}>
              <div className="message-content">
                {message.type === 'vibe' ? (
                  <div className="vibe-message-container">
                    <div className="vibe-message-card">
                      <h4 className="vibe-title">IT'S GIVING</h4>
                      {editingMessageId === message.id ? (
                        <div className="vibe-edit-inline">
                          <textarea
                            className="vibe-edit-textarea-inline"
                            value={`${editedBookTitle} by ${editedAuthor}`}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Parse the input to extract book title and author
                              const byIndex = value.lastIndexOf(' by ');
                              if (byIndex > 0) {
                                const newTitle = value.substring(0, byIndex).trim();
                                const newAuthor = value.substring(byIndex + 4).trim();
                                setEditedBookTitle(newTitle);
                                setEditedAuthor(newAuthor);
                              } else {
                                // If no " by " found, treat entire input as title
                                setEditedBookTitle(value);
                                setEditedAuthor('');
                              }
                            }}
                            placeholder="Book Title by Author Name"
                            rows={2}
                            autoFocus
                          />
                          <div className="vibe-edit-buttons-inline">
                            <button className="vibe-save-button-inline" onClick={handleSaveEdit}>
                              Save
                            </button>
                            <button className="vibe-cancel-button-inline" onClick={handleCancelEdit}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="vibe-book-info">
                            <div className="vibe-book-emoji">📚</div>
                            <div className="vibe-book-title-container">
                              <h3 className="vibe-book-title">{message.bookTitle || 'Unknown Book'}</h3>
                              {/* Share button temporarily hidden - keeping code for future use */}
                              {/* <button className="vibe-share-icon" onClick={handleShare} title="Share">
                                🔗
                              </button> */}
                            </div>
                            <p className="vibe-book-author">by {message.author || 'Unknown Author'}</p>
                          </div>
                          <div className="vibe-action-buttons">
                            <button className="vibe-edit-button" onClick={() => handleEditVibe(message.id, message.content, message.bookTitle, message.author)}>
                              ✏️ Edit
                            </button>
                            <button className={`vibe-confirm-button ${isConfirmed ? 'confirmed' : ''}`} onClick={handleConfirm}>
                              {isConfirmed ? 'Confirmed' : 'Confirm'}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  message.content
                )}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message bot">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          
          <div ref={messagesEndRef} />
        </div>

        {!conversationComplete && (
          <div className="chatbot-input">
            <div className="input-container">
              <textarea
                ref={inputRef}
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your answer here..."
                rows="2"
                maxLength="500"
                disabled={isLoading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>
            <div className="input-footer">
              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(questionCount / 9) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div className="input-tip">Press enter to send</div>
            </div>
          </div>
        )}

        {conversationComplete && showCompletionMessage && (
          <div className="chatbot-complete">
            <div className="completion-message">
              <p>Your preliminary LUV chat is complete.<br />Sign up to create your LUV.</p>
            </div>
            <div className="chatbot-footer-buttons">
              <button className="signup-button" onClick={handleSignUp}>
                Sign Up
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    )}

    </>
  );
};

export default DatingChatBot;
